import {
  Box,
  CircularProgress,
  IconButton,
  TableCell,
  Menu,
  MenuItem,
  TableRow,
  Theme,
  Tooltip,
  Typography,
  Grid
} from "@mui/material"
import {
  DownloadIcon,
  InfoIconOutlined,
  ShareIcon
} from "../assets/SvgIcons"
import CustomTable from "./investorOverview/common/customTable"
import {
  Order,
  PaginationObj,
  SortingObj,
  TableHeaderData
} from "./investorOverview/types"
import { ClientStatementReportsList } from "../redux-store/types/onBoarding"
import dayjs from "dayjs"

const tableHeaderData: TableHeaderData[] = [
  {
    id: 'clientName',
    label: 'Client Name',
    align: 'left',
    isSortable: true,
  },
  {
    id: 'consolidatedLevel',
    label: 'Filter Type',
    align: 'center',
    isSortable: false,
  },
  {
    id: 'reportDate',
    label: 'Report Date',
    align: 'center',
    isSortable: false,
  },
  {
    id: 'createdAt',
    label: 'Initiated Date',
    align: 'center',
    isSortable: true,
  },
  {
    id: 'status',
    label: 'Status',
    align: 'center',
    isSortable: false,
  },
  {
    id: 'action',
    label: '',
    align: 'center',
    isSortable: false,
  }
]

const statusColorMap: { [key: string]: string } = {
  pending: "warning.500",
  success: "success.500",
  failed: "error.500"
}

function DataTable({
  tableBodyData,
  handleReportDownload,
  isDownloading,
  selectedJobIdForDownload,
  handleShareMenuOpen,
  shareMenuAnchorEl,
  handleShareMenuClose,
  handleShareVia,
  isSharing,
  selectedJobIdForShare
}: {
  tableBodyData: Array<ClientStatementReportsList>;
  handleReportDownload: (jobId: string, clientName: string, shareVia?: 'download' | 'email' | 'whatsapp') => void;
  isDownloading: boolean;
  selectedJobIdForDownload: string | null;
  handleShareMenuOpen: (event: React.MouseEvent<HTMLElement>, jobId: string) => void;
  shareMenuAnchorEl: { [key: string]: HTMLElement | null };
  handleShareMenuClose: (jobId: string) => void;
  handleShareVia: (jobId: string, clientName: string, shareVia: 'email' | 'whatsapp') => void;
  isSharing: boolean;
  selectedJobIdForShare: string | null;
}): JSX.Element {

  const getFilterType = (consolidatedLevel: string) => {
    if (consolidatedLevel === "PAN") {
      return "CLIENT"
    }
    return consolidatedLevel
  }


  return (
    <>
      {
        tableBodyData.map((obj: ClientStatementReportsList, index: number) => {
          return (
            <TableRow
              key={`${obj.id}-${index}`}
              sx={(theme: Theme) => ({
                height: "4.5em",
                '& td': {
                  padding: "5px",
                  height: "4.5em",
                  borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                },
                '& td:first-of-type': {
                  paddingLeft: "0px"
                },
              })}
            >
              <TableCell align="left">
                <Typography
                  variant="body_500"
                  sx={(theme: Theme) => ({
                    lineHeight: "20px",
                    letterSpacing: "0.02px",
                    color: theme.palette.text.primary,
                  })}
                >
                  {`${obj?.clientName ?? ""}`?.length ? obj?.clientName : "-"}{" "}
                  {`${obj?.clientCode ?? ""}`?.length ? "(" + obj?.clientCode + ")" : "(-)"}{" "}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography
                  variant="body_500"
                  sx={(theme: Theme) => ({
                    lineHeight: "20px",
                    letterSpacing: "0.02px",
                    color: theme.palette.text.primary,
                  })}
                >
                  {getFilterType(obj?.consolidatedLevel ?? "-")}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography
                  variant="body_500"
                  sx={(theme: Theme) => ({
                    lineHeight: "20px",
                    letterSpacing: "0.02px",
                    color: theme.palette.text.primary,
                  })}
                >
                  {
                    `${obj?.reportDate ?? ""}`?.length ?
                      dayjs(obj?.reportDate).format('DD/MM/YYYY') : "-"
                  }
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography
                  variant="body_500"
                  sx={(theme: Theme) => ({
                    lineHeight: "20px",
                    letterSpacing: "0.02px",
                    color: theme.palette.text.primary,
                  })}
                >
                  {
                    `${obj?.createdAt ?? ""}`?.length ?
                      dayjs(obj?.createdAt).format('DD/MM/YYYY HH:mm:ss') : "-"
                  }
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography
                  variant="body_500"
                  sx={{
                    lineHeight: "20px",
                    letterSpacing: "0.02px",
                    color: statusColorMap[`${obj?.status ?? ""}`?.toLowerCase()]
                  }}
                >
                  {obj?.status ?? ""}
                </Typography>
              </TableCell>
              <TableCell align="center">
                {(`${obj?.status ?? ""}`?.toLowerCase() === "failed") && (
                  <Tooltip
                    slotProps={{
                      tooltip: {
                        sx: {
                          margin: "0px !important",
                          backgroundColor: "transparent",
                        }
                      }
                    }}
                    title={
                      <Box sx={(theme: Theme) => ({
                        borderRadius: '10px',
                        backgroundColor: theme.palette.customColors.cinder,
                        padding: '10px',
                        display: 'flex',
                        flexDirection: 'column',
                        rowGap: '10px',
                      })}>
                        <Typography variant='subtitle_600'>
                          Failed to generate Report. Please contact Admin.
                        </Typography>
                      </Box>
                    }
                  >
                    <IconButton>
                      <InfoIconOutlined />
                    </IconButton>
                  </Tooltip>
                )}
                {(`${obj?.status ?? ""}`?.toLowerCase() === "success") && (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    {(isDownloading && selectedJobIdForDownload === obj.jobId) ? (
                      <CircularProgress
                        color="info"
                        size={20}
                      />
                    ) : (
                      <IconButton
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handleReportDownload(obj.jobId, obj.clientName, 'download')}
                      >
                        <DownloadIcon />
                      </IconButton>
                    )}
                    {(isSharing && selectedJobIdForShare === obj.jobId) ? (
                      <CircularProgress
                        color="info"
                        size={20}
                      />
                    ) : (
                      <>
                        <IconButton
                          sx={{ cursor: 'pointer' }}
                          onClick={(e) => handleShareMenuOpen(e, obj.jobId)}
                        >
                          <ShareIcon />
                        </IconButton>
                        <Menu
                          anchorEl={shareMenuAnchorEl[obj.jobId] || null}
                          open={Boolean(shareMenuAnchorEl[obj.jobId])}
                          onClose={() => handleShareMenuClose(obj.jobId)}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                          }}
                        >
                          <MenuItem onClick={() => handleShareVia(obj.jobId, obj.clientName, 'email')}>
                            Email
                          </MenuItem>
                          {/* <MenuItem onClick={() => handleShareVia(obj.jobId, obj.clientName, 'whatsapp')}>
                            WhatsApp
                          </MenuItem> */}
                        </Menu>
                      </>
                    )}
                  </Box>
                )}
              </TableCell>
            </TableRow >
          )
        })
      }
    </>
  )
}

export function MobileResponsiveDataTable({
  tableBodyData,
  handleReportDownload,
  isDownloading,
  selectedJobIdForDownload,
  tableHeaderData,
  handleShareMenuOpen,
  shareMenuAnchorEl,
  handleShareMenuClose,
  handleShareVia,
  isSharing,
  selectedJobIdForShare,
}: {
  tableBodyData: Array<ClientStatementReportsList>;
  handleReportDownload: (jobId: string, clientName: string, shareVia?: 'download' | 'email' | 'whatsapp') => void;
  isDownloading: boolean;
  selectedJobIdForDownload: string | null;
  tableHeaderData: TableHeaderData[];
  handleShareMenuOpen: (event: React.MouseEvent<HTMLElement>, jobId: string) => void;
  shareMenuAnchorEl: { [key: string]: HTMLElement | null };
  handleShareMenuClose: (jobId: string) => void;
  handleShareVia: (jobId: string, clientName: string, shareVia: 'email' | 'whatsapp') => void;
  isSharing: boolean;
  selectedJobIdForShare: string | null;
}): JSX.Element {
  const getFilterType = (consolidatedLevel: string) => {
    if (consolidatedLevel === "PAN") {
      return "CLIENT";
    }
    return consolidatedLevel;
  };

  const getLabel = (id: string) =>
    tableHeaderData.find((col) => col.id === id)?.label ?? id;

  return (
    <>
      {tableBodyData.map((obj, index) => (
        <Box
          key={`${obj.id}-${index}`}
          sx={(theme: Theme) => ({
            border: `0.5px solid ${theme.palette.text.secondary}40`,
            borderRadius: "12px",
            p: 2,
            mb: 2,
            backgroundColor: theme.palette.background.paper,
            overflowX: 'hidden',
          })}
        >
          {/* clientname and client value adding */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={8}>
              <Typography
                variant="body_500"
                sx={(theme: Theme) => ({
                  fontSize: "12px",
                  color: theme.palette.primary[500],
                  textAlign: 'left',
                  fontWeight: 600,
                })}
              >
                {getLabel('clientName')}
              </Typography>
              <Typography
                variant="body_500"
                sx={(theme: Theme) => ({
                  color: theme.palette.text.primary,
                  textAlign: 'left',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                  fontSize: '15px',
                  fontWeight: 500,
                })}
              >
                {`${obj?.clientName ?? ""}`?.length ? obj?.clientName : "-"}{" "}
                {`${obj?.clientCode ?? ""}`?.length
                  ? "(" + obj?.clientCode + ")"
                  : "(-)"}
              </Typography>
            </Grid>
            <Grid item xs={4} sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}>
              {/* Actions */}
              {(`${obj?.status ?? ""}`?.toLowerCase() === "failed") && (
                <Tooltip
                  slotProps={{
                    tooltip: {
                      sx: {
                        margin: "0px !important",
                        backgroundColor: "transparent",
                      },
                    },
                  }}
                  title={
                    <Box
                      sx={(theme: Theme) => ({
                        borderRadius: "10px",
                        backgroundColor: theme.palette.customColors.cinder,
                        padding: "10px",
                        display: "flex",
                        flexDirection: "column",
                        rowGap: "10px",
                      })}
                    >
                      <Typography variant="subtitle_600">
                        Failed to generate Report. Please contact Admin.
                      </Typography>
                    </Box>
                  }
                >
                  <IconButton>
                    <InfoIconOutlined />
                  </IconButton>
                </Tooltip>
              )}

              {(`${obj?.status ?? ""}`?.toLowerCase() === "success") && (
                <>
                  {(isDownloading && selectedJobIdForDownload === obj.jobId) ? (
                    <CircularProgress color="info" size={20} />
                  ) : (
                    <IconButton
                      sx={{ cursor: "pointer" }}
                      onClick={() => handleReportDownload(obj.jobId, obj.clientName, 'download')}
                    >
                      <DownloadIcon />
                    </IconButton>
                  )}
                  {(isSharing && selectedJobIdForShare === obj.jobId) ? (
                    <CircularProgress color="info" size={20} />
                  ) : (
                    <>
                      <IconButton
                        sx={{ cursor: "pointer" }}
                        onClick={(e) => handleShareMenuOpen(e, obj.jobId)}
                      >
                        <ShareIcon />
                      </IconButton>
                      <Menu
                        anchorEl={shareMenuAnchorEl[obj.jobId] || null}
                        open={Boolean(shareMenuAnchorEl[obj.jobId])}
                        onClose={() => handleShareMenuClose(obj.jobId)}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                      >
                        <MenuItem onClick={() => handleShareVia(obj.jobId, obj.clientName, 'email')}>
                          Email
                        </MenuItem>
                        <MenuItem onClick={() => handleShareVia(obj.jobId, obj.clientName, 'whatsapp')}>
                          WhatsApp
                        </MenuItem>
                      </Menu>
                    </>
                  )}
                </>
              )}
            </Grid>
          </Grid>

          {/* Grid of Details */}
          <Grid container spacing={2}>
            {/* Filter Type */}
            <Grid item xs={6}>
              <Typography
                variant="body_500"
                sx={(theme: Theme) => ({
                  fontSize: "12px",
                  color: theme.palette.text.secondary,
                  textAlign: 'left',
                })}
              >
                {getLabel("consolidatedLevel")}
              </Typography>
              <Typography
                variant="body_500"
                sx={(theme: Theme) => ({
                  color: theme.palette.text.primary,
                  textAlign: 'left',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                })}
              >
                {getFilterType(obj?.consolidatedLevel ?? "-")}
              </Typography>
            </Grid>

            {/* Report Date */}
            <Grid item xs={6}>
              <Typography
                variant="body_500"
                sx={(theme: Theme) => ({
                  fontSize: "12px",
                  color: theme.palette.text.secondary,
                  textAlign: 'right',
                })}
              >
                {getLabel("reportDate")}
              </Typography>
              <Typography
                variant="body_500"
                sx={(theme: Theme) => ({
                  color: theme.palette.text.primary,
                  textAlign: 'right',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                })}
              >
                {`${obj?.reportDate ?? ""}`?.length
                  ? dayjs(obj?.reportDate).format("DD/MM/YYYY")
                  : "-"}
              </Typography>
            </Grid>

            {/* Initiated Date */}
            <Grid item xs={6}>
              <Typography
                variant="body_500"
                sx={(theme: Theme) => ({
                  fontSize: "12px",
                  color: theme.palette.text.secondary,
                  textAlign: 'left',
                })}
              >
                {getLabel("createdAt")}
              </Typography>
              <Typography
                variant="body_500"
                sx={(theme: Theme) => ({
                  color: theme.palette.text.primary,
                  textAlign: 'left',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                })}
              >
                {`${obj?.createdAt ?? ""}`?.length
                  ? dayjs(obj?.createdAt).format("DD/MM/YYYY HH:mm:ss")
                  : "-"}
              </Typography>
            </Grid>

            {/* Status */}
            <Grid item xs={6}>
              <Typography
                variant="body_500"
                sx={(theme: Theme) => ({
                  fontSize: "12px",
                  color: theme.palette.text.secondary,
                  textAlign: 'right',
                })}
              >
                {getLabel("status")}
              </Typography>
              <Typography
                variant="body_500"
                sx={(theme: Theme) => ({
                  color:
                    statusColorMap[`${obj?.status ?? ""}`?.toLowerCase()],
                  textAlign: 'right',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                })}
              >
                {obj?.status ?? ""}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      ))}
    </>
  );
}



interface ClientStatementTableProps {
  isLoading: boolean;
  tableData: Array<ClientStatementReportsList>;
  handleTableSort: (orderBy: string, order: Order) => void;
  tablePaginationObj: PaginationObj;
  handleTableRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleTablePageChange: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void;
  tableSortingObj: SortingObj;
  handleReportDownload: (jobId: string, clientName: string, shareVia?: 'download' | 'email' | 'whatsapp') => void;
  isDownloading: boolean;
  selectedJobIdForDownload: string | null;
  handleShareMenuOpen: (event: React.MouseEvent<HTMLElement>, jobId: string) => void;
  shareMenuAnchorEl: { [key: string]: HTMLElement | null };
  handleShareMenuClose: (jobId: string) => void;
  handleShareVia: (jobId: string, clientName: string, shareVia: 'email' | 'whatsapp') => void;
  isSharing: boolean;
  selectedJobIdForShare: string | null;
}


export default function ClientStatementTable({
  isLoading,
  tableData,
  handleTableSort,
  tablePaginationObj,
  handleTableRowsPerPageChange,
  handleTablePageChange,
  tableSortingObj,
  handleReportDownload,
  isDownloading,
  selectedJobIdForDownload,
  handleShareMenuOpen,
  shareMenuAnchorEl,
  handleShareMenuClose,
  handleShareVia,
  isSharing,
  selectedJobIdForShare
}: ClientStatementTableProps): JSX.Element {
  return (
    <CustomTable
      isLoading={isLoading}
      tableBody={
        <DataTable
          tableBodyData={tableData}
          handleReportDownload={handleReportDownload}
          isDownloading={isDownloading}
          selectedJobIdForDownload={selectedJobIdForDownload}
          handleShareMenuOpen={handleShareMenuOpen}
          shareMenuAnchorEl={shareMenuAnchorEl}
          handleShareMenuClose={handleShareMenuClose}
          handleShareVia={handleShareVia}
          isSharing={isSharing}
          selectedJobIdForShare={selectedJobIdForShare}
        />
      }
      tableData={tableData}
      tableHeaderData={tableHeaderData}
      handleSort={handleTableSort}
      tableHeadRowCustomStyles={{
        '& th:first-of-type': {
          paddingLeft: "0px"
        }
      }}

      tableBodyCustomStyles={{
        '& tr:last-child td': {
          border: 0
        }
      }}
      loadingRowsNum={7}
      tableDataCount={tablePaginationObj.totalRows}
      rowsPerPage={tablePaginationObj.rowsPerPage}
      rowsPerPageOptions={[5, 10, 15, 20]}
      handleRowsPerPageChange={handleTableRowsPerPageChange}
      handlePageChange={handleTablePageChange}
      currentPage={tablePaginationObj.currentPage}
      order={tableSortingObj.order}
      orderBy={tableSortingObj.orderBy}
      manualResponsiveBody={true}
      hideResponsiveHeader={true}
      ManualResponsiveBodyComponent={
        <MobileResponsiveDataTable
          tableBodyData={tableData}
          handleReportDownload={handleReportDownload}
          isDownloading={isDownloading}
          selectedJobIdForDownload={selectedJobIdForDownload}
          tableHeaderData={tableHeaderData}
          handleShareMenuOpen={handleShareMenuOpen}
          shareMenuAnchorEl={shareMenuAnchorEl}
          handleShareMenuClose={handleShareMenuClose}
          handleShareVia={handleShareVia}
          isSharing={isSharing}
          selectedJobIdForShare={selectedJobIdForShare}
        />
      }
    />
  )
}